'use client'

import { usePayment } from "@/context/payment";
import { SelectChangeEvent, Stack, Alert } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import '@/components/styles/common.scss';

import React from "react";
const Input = dynamic(() => import('@/components/common/input'), { ssr: false });
const Select = dynamic(() => import('@/components/common/select'), { ssr: false });
const ServiceFee = dynamic(() => import('@/components/pages/payments/service-fee'), { ssr: false });
const ButtonStepper = dynamic(() => import('@/components/common/button-stepper'), { ssr: false });
const PhoneInput = dynamic(() => import('@/components/common/phone-input'), { ssr: false });

export default function Page() {
    const router = useRouter();
    const { GetServices, formik, loading,
        application, application_id, services,
        package_id, applications,
        phone_codes, payment_gateways } = usePayment();
    const fullNameRef = React.useRef<HTMLDivElement>(null);
    const emailRef = React.useRef<HTMLDivElement>(null);
    const numberRef = React.useRef<HTMLDivElement>(null);
    const paymentMethodRef = React.useRef<HTMLDivElement>(null);
    const programRef = React.useRef<HTMLDivElement>(null);
    const serviceIdRef = React.useRef<HTMLDivElement>(null);
    const fieldRefMap = React.useMemo(() => ({
        full_name: fullNameRef,
        email: emailRef,
        number: numberRef,
        payment_method: paymentMethodRef,
        program: programRef,
        service_id: serviceIdRef,
    }), [fullNameRef, emailRef, numberRef, paymentMethodRef, programRef, serviceIdRef]);
    const lastScrolledFieldRef = React.useRef<string | null>(null);
    const previousSubmitCountRef = React.useRef<number>(0);

    React.useEffect(() => {
        if (formik.submitCount === 0) return;

        if (formik.submitCount !== previousSubmitCountRef.current) {
            previousSubmitCountRef.current = formik.submitCount;
            lastScrolledFieldRef.current = null;
        }

        const fieldOrder: (keyof typeof fieldRefMap)[] = ['full_name', 'email', 'number', 'payment_method', 'service_id', 'program'];
        const firstErrorField = fieldOrder.find((key) => {
            const errorValue = formik.errors[key];
            if (!errorValue) return false;
            if (typeof errorValue === 'string') {
                return errorValue.trim().length > 0;
            }
            return true;
        });

        if (!firstErrorField) return;
        if (lastScrolledFieldRef.current === firstErrorField) return;
        lastScrolledFieldRef.current = firstErrorField;

        const node = fieldRefMap[firstErrorField].current;
        if (node) {
            requestAnimationFrame(() => {
                node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            });
        }
    }, [fieldRefMap, formik.errors, formik.submitCount]);

    React.useLayoutEffect(() => {

        GetServices();
    }, []);

    // انتخاب خودکار payment_method بر اساس prefix
    React.useEffect(() => {
        if (!formik.values.prefix || payment_gateways.length === 0) return;

        const isIran = formik.values.prefix.includes("IR");

        // پیدا کردن payment_method مناسب
        const targetMethod = isIran
            ? payment_gateways.find(gw => gw.value.toLowerCase().includes('rial'))
            : payment_gateways.find(gw => gw.value.toLowerCase().includes('stripe'));

        if (targetMethod && formik.values.payment_method !== targetMethod.value) {
            formik.setFieldValue('payment_method', targetMethod.value);
        }
    }, [formik.values.prefix, payment_gateways]);

    const t = useTranslations('payments.new_payment.inputs');
    const actions = useTranslations('actions');
    const servicesCard = useTranslations('payments.new_payment.services_card');

    return <form onSubmit={formik.handleSubmit}>
        <Stack
            direction={'column'}
            useFlexGap
            spacing={4}
            paddingTop={4}
        >
            <Grid
                container
                spacing={4}
            >
                <Grid
                    container
                    size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                    ref={fullNameRef}
                >
                    <Input
                        disabled={loading}
                        key={'full_name'}
                        label={t('full_name.label')}
                        placeholder={t('full_name.placeholder')}
                        id='full_name'
                        name='full_name'
                        value={formik.values.full_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                        helper_text={formik.touched.full_name && formik.errors.full_name}
                    />
                </Grid>

                <Grid
                    container
                    size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                    ref={emailRef}
                >
                    <Input
                        disabled={loading}
                        key={'email'}
                        label={t('email.label')}
                        placeholder={t('email.placeholder')}
                        id='email'
                        name='email'
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helper_text={formik.touched.email && formik.errors.email}
                    />
                </Grid>

                <Grid
                    container
                    size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                    ref={numberRef}
                >

                    <PhoneInput
                        disabled={loading}
                        list={phone_codes.map(cnt => ({ name: cnt.label, value: cnt.value }))}
                        key='number'
                        label={t('number.label')}
                        placeholder={t('number.placeholder')}
                        id='number'
                        name='number'
                        value={formik.values.number}
                        onChange={formik.handleChange}
                        prefix_val={formik.values.prefix}
                        onPrefixChange={(e: SelectChangeEvent<string>) => { formik.setFieldValue('prefix', e.target.value) }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.number && Boolean(formik.errors.number)}
                        helper_text={formik.touched.number && formik.errors.number}
                    />
                </Grid>

                <Grid
                    container
                    size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                    ref={paymentMethodRef}
                >
                    <Select
                        disabled={loading}
                        key={'payment_method'}
                        id='payment_method'
                        list={payment_gateways.map(cnt => ({ name: cnt.label, value: cnt.value }))}
                        name='payment_method'
                        val={formik.values.payment_method}
                        onBlur={formik.handleBlur}
                        handleChange={formik.handleChange}
                        error={formik.touched.payment_method && Boolean(formik.errors.payment_method)}
                        helper_text={formik.touched.payment_method && formik.errors.payment_method}
                        label={t('payment_method.label')}
                    />
                </Grid>

                <div className={'__ServiceFee__'} ref={serviceIdRef}>
                    <ServiceFee />
                    {formik.touched.service_id && formik.errors.service_id && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {formik.errors.service_id}
                        </Alert>
                    )}
                </div>
                {services.find(item => item.id.toString() === formik.values.service_id)?.title === 'Application Fee' &&
                    <>
                        <Grid
                            className={'__newPay_info_Page__cls1_'}
                            container
                            size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                            ref={programRef}
                        >
                            <Select
                                disabled={loading || application_id !== null || package_id != null}
                                key={'program'}
                                id='program'
                                list={application_id !== null ?
                                    [{ name: application?.program?.name || "", value: application?.program?.name || "" }]
                                    :
                                    applications.map(app => ({ name: app.program?.name || "", value: app.program?.name || "" }))
                                }
                                label={servicesCard('inputs.program')}
                                name='program'
                                val={formik.values.program}
                                onBlur={formik.handleBlur}
                                handleChange={formik.handleChange}
                                error={formik.touched.program && Boolean(formik.errors.program)}
                                helper_text={formik.touched.program && formik.errors.program}
                            />
                        </Grid>

                        <Grid
                            container
                            size={{ lg: 6, sm: 12, md: 12, xs: 12 }}
                            className={'__newPay_info_Page__cls2_'}
                        >
                            <Input
                                disabled
                                key={'amount'}
                                type="number"
                                label={servicesCard('inputs.application_fee')}
                                placeholder='Program Application Fee'
                                id='amount'
                                name='amount'
                                value={formik.values.amount}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.amount && Boolean(formik.errors.amount)}
                                helper_text={formik.touched.amount && formik.errors.amount}
                            />
                        </Grid>
                    </>
                }
            </Grid>


            <ButtonStepper
                className={'__newPay_info_Page__cls3_'}
                loading={loading}
                submit_button_text={actions('Complete Payment')}
                skip_button={false}
                handleSubmit={() => formik.handleSubmit}
                handleBack={() => { router.push('/payments') }}
            />

        </Stack>
    </form>
};
